<script setup>
import { computed } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        required: true,
    },
    items: {
        type: Array,
        required: true,
    },
    size: {
        type: String,
        default: 'sm',
        validator: value => ['xs', 'sm', 'md'].includes(value),
    },
    appearance: {
        type: String,
        default: 'bar',
        validator: value => ['bar', 'inline'].includes(value),
    },
    ariaLabel: {
        type: String,
        default: 'Filter options',
    },
});

const emit = defineEmits(['update:modelValue', 'change']);

const rootClasses = computed(() => {
    if (props.appearance === 'inline') {
        return 'flex items-center gap-1';
    }

    return 'flex items-center gap-1 px-4 py-2 border-b border-gray-100 bg-gray-50';
});

const sizeClasses = computed(() => {
    const sizes = {
        xs: 'px-2 py-0.5 text-[10px]',
        sm: 'px-3 py-1 text-xs',
        md: 'px-3.5 py-1.5 text-sm',
    };

    return sizes[props.size];
});

const isSelected = (item) => item.value === props.modelValue;

const itemClasses = (item) => [
    sizeClasses.value,
    'rounded-full font-medium transition-colors',
    item.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
    isSelected(item)
        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
        : 'text-gray-500 hover:text-gray-700 border border-transparent',
];

const shouldShowCount = (item) => item.count !== undefined && item.count !== null;

const selectItem = (item) => {
    if (item.disabled || isSelected(item)) {
        return;
    }

    emit('update:modelValue', item.value);
    emit('change', item);
};
</script>

<template>
    <div
        :class="rootClasses"
        role="group"
        :aria-label="ariaLabel"
    >
        <button
            v-for="item in items"
            :key="item.value"
            type="button"
            :aria-pressed="isSelected(item)"
            :disabled="item.disabled"
            :class="itemClasses(item)"
            @click="selectItem(item)"
        >
            <span>{{ item.label }}</span>
            <span
                v-if="shouldShowCount(item)"
                class="ml-1 text-gray-400"
            >
                ({{ item.count }})
            </span>
        </button>
    </div>
</template>
